import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import PageHero from 'tio-reimagined/components/page-hero';
import NavCard from 'tio-reimagined/components/nav-card';
import SectionHeader from 'tio-reimagined/components/section-header';
import SectionPanel from 'tio-reimagined/components/section-panel';
import {
  PhArrowLeft,
  PhBriefcase,
  PhGraduationCap,
  PhGridFour,
  PhLayout,
  PhLightning,
  PhMapTrifold,
  PhPiggyBank,
  PhRocket,
  PhStar,
  PhTarget,
  PhTrendUp,
} from 'ember-phosphor-icons';

<template>
  {{! template-lint-disable no-bare-strings }}
  <VStack class="w-full min-w-0">
    <div class="mb-4">
      <LinkTo
        @route="authenticated.reimagined.components"
        class="inline-flex items-center gap-2 text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 font-medium"
      >
        <PhArrowLeft @size={{18}} />
        Back to Components
      </LinkTo>
    </div>

    <PageHero
      @title="Layout & Navigation"
      @subtitle="PageHero, NavCard, SectionHeader, and SectionPanel for building page structures."
      @icon={{PhLayout}}
      @theme="teal"
      @decoration="circles"
    >
      <:actions>
        <Chip @intent="primary">4 Components</Chip>
      </:actions>
    </PageHero>

    <div class="space-y-8">

      {{! ========================================
            PageHero
            ======================================== }}
      <Section>
        <:header>
          <div class="flex items-center gap-2">
            <PhLayout @size={{20}} class="text-violet-600" />
            <span>PageHero</span>
            <Chip @size="sm" @intent="primary">11 Themes</Chip>
          </div>
        </:header>
        <:body>
          <Text @intent="muted" class="mb-4">
            Gradient hero banner with two modes:
            <strong>block mode</strong>
            (icon/badge/footer blocks) and
            <strong>convenience mode</strong>
            (@title/@subtitle/@icon with stat/actions/content blocks). 11 theme presets, 4 SVG
            decoration patterns, customizable rounding.
          </Text>

          <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-4">
            {{! Convenience mode demo }}
            <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Convenience Mode:</Text>
            <PageHero
              @title="Section 127 Optimizer"
              @subtitle="Maximize your tax-free education benefit"
              @icon={{PhPiggyBank}}
              @theme="emerald"
              @decoration="circles"
            >
              <:stat>
                <div class="text-3xl font-bold">$5,250</div>
                <div class="text-sm text-white/70">Annual Limit</div>
              </:stat>
              <:actions>
                <Button
                  @intent="default"
                  @appearance="outlined"
                  @size="sm"
                  class="text-white border-white/40 hover:bg-white/10"
                >
                  Learn More
                </Button>
              </:actions>
            </PageHero>

            {{! Block mode demo }}
            <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Block Mode:</Text>
            <PageHero @theme="violet" @decoration="circles">
              <:icon>
                <PhGraduationCap @size={{32}} />
                <Heading @level={{2}} class="text-white">Violet + Circles</Heading>
              </:icon>
              <:badge>
                <Chip @intent="primary">Badge Slot</Chip>
              </:badge>
              <:default>
                <Text class="text-white/80">Block mode with icon, badge, and footer blocks.</Text>
              </:default>
              <:footer>
                <Text class="text-white/70 text-sm">Footer block with top border divider</Text>
              </:footer>
            </PageHero>

            {{! Theme grid }}
            <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">All 11 Themes:</Text>
            <div class="grid grid-cols-3 lg:grid-cols-4 gap-3">
              <PageHero @theme="violet" @rounded="lg"><span
                  class="font-semibold text-white text-sm"
                >Violet</span></PageHero>
              <PageHero @theme="emerald" @rounded="lg"><span
                  class="font-semibold text-white text-sm"
                >Emerald</span></PageHero>
              <PageHero @theme="blue" @rounded="lg"><span
                  class="font-semibold text-white text-sm"
                >Blue</span></PageHero>
              <PageHero @theme="amber" @rounded="lg"><span
                  class="font-semibold text-white text-sm"
                >Amber</span></PageHero>
              <PageHero @theme="teal" @rounded="lg"><span
                  class="font-semibold text-white text-sm"
                >Teal</span></PageHero>
              <PageHero @theme="indigo" @rounded="lg"><span
                  class="font-semibold text-white text-sm"
                >Indigo</span></PageHero>
              <PageHero @theme="sky" @rounded="lg"><span
                  class="font-semibold text-white text-sm"
                >Sky</span></PageHero>
              <PageHero @theme="rose" @rounded="lg"><span
                  class="font-semibold text-white text-sm"
                >Rose</span></PageHero>
              <PageHero @theme="red" @rounded="lg"><span
                  class="font-semibold text-white text-sm"
                >Red</span></PageHero>
              <PageHero @theme="gray" @rounded="lg"><span
                  class="font-semibold text-white text-sm"
                >Gray</span></PageHero>
              <PageHero @theme="fuchsia" @rounded="lg"><span
                  class="font-semibold text-white text-sm"
                >Fuchsia</span></PageHero>
            </div>
          </div>

          <details class="border dark:border-gray-700 rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Code Examples
            </summary>
            <pre
              class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
            ><code>{{! Convenience mode }}
                &lt;PageHero @title="Section 127 Optimizer" @subtitle="Maximize your tax-free
                benefit" @icon=&#123;&#123;PhPiggyBank&#125;&#125; @theme="emerald"
                @decoration="circles"&gt; &lt;:stat&gt;&lt;div class="text-3xl
                font-bold"&gt;$5,250&lt;/div&gt;&lt;/:stat&gt; &lt;:actions&gt;&lt;Button&gt;Learn
                More&lt;/Button&gt;&lt;/:actions&gt; &lt;/PageHero&gt;

                {{! Block mode }}
                &lt;PageHero @theme="violet" @decoration="circles"&gt;
                &lt;:icon&gt;&lt;PhGraduationCap @size=&#123;&#123;32&#125;&#125; /&gt; &lt;Heading
                @level=&#123;&#123;1&#125;&#125;
                class="text-white"&gt;Title&lt;/Heading&gt;&lt;/:icon&gt; &lt;:default&gt;Body
                content&lt;/:default&gt; &lt;:footer&gt;Footer content&lt;/:footer&gt;
                &lt;/PageHero&gt;</code></pre>
          </details>
        </:body>
      </Section>

      {{! ========================================
            NavCard
            ======================================== }}
      <Section>
        <:header>
          <div class="flex items-center gap-2">
            <PhMapTrifold @size={{20}} class="text-violet-600" />
            <span>NavCard</span>
            <Chip @size="sm" @intent="success">3 Layouts</Chip>
          </div>
        </:header>
        <:body>
          <Text @intent="muted" class="mb-4">
            Unified navigation card with vertical, horizontal, and gradient layout variants.
            Consolidates the former QuickActionLink, ActionLinkCard, and ToolCard components.
          </Text>

          <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-4">
            <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Vertical (default):</Text>
            <div class="grid grid-cols-3 gap-4">
              <NavCard
                @route="authenticated.reimagined"
                @icon={{PhRocket}}
                @title="Get Started"
                @subtitle="Begin your journey"
                @color="violet"
              />
              <NavCard
                @route="authenticated.reimagined"
                @icon={{PhTarget}}
                @title="Explore"
                @subtitle="Find programs"
                @color="teal"
              />
              <NavCard
                @route="authenticated.reimagined"
                @icon={{PhStar}}
                @title="Favorites"
                @subtitle="Saved items"
                @color="amber"
              />
            </div>

            <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Horizontal:</Text>
            <div class="space-y-2">
              <NavCard
                @route="authenticated.reimagined"
                @icon={{PhBriefcase}}
                @title="Career Explorer"
                @subtitle="Browse career paths"
                @layout="horizontal"
                @color="indigo"
              />
              <NavCard
                @route="authenticated.reimagined"
                @icon={{PhGraduationCap}}
                @title="Programs"
                @subtitle="View eligible programs"
                @layout="horizontal"
                @color="emerald"
              />
            </div>

            <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Gradient:</Text>
            <div class="grid grid-cols-2 gap-4">
              <NavCard
                @route="authenticated.reimagined"
                @icon={{PhLightning}}
                @title="Skills Assessment"
                @subtitle="Evaluate your skills"
                @layout="gradient"
                @color="violet"
              />
              <NavCard
                @route="authenticated.reimagined"
                @icon={{PhTrendUp}}
                @title="Career Growth"
                @subtitle="Track progress"
                @layout="gradient"
                @color="teal"
              />
            </div>
          </div>

          <details class="border dark:border-gray-700 rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Code Examples
            </summary>
            <pre
              class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
            ><code>&lt;NavCard @route="authenticated.reimagined"
                @icon=&#123;&#123;PhRocket&#125;&#125; @title="Get Started" @subtitle="Begin your
                journey" @color="violet" /&gt; &lt;NavCard @route="authenticated.reimagined"
                @icon=&#123;&#123;PhBriefcase&#125;&#125; @title="Career Explorer"
                @layout="horizontal" @color="indigo" /&gt; &lt;NavCard
                @route="authenticated.reimagined" @icon=&#123;&#123;PhLightning&#125;&#125;
                @title="Skills Assessment" @layout="gradient" @color="violet" /&gt;</code></pre>
          </details>
        </:body>
      </Section>

      {{! ========================================
            SectionHeader + SectionPanel
            ======================================== }}
      <Section>
        <:header>
          <div class="flex items-center gap-2">
            <PhGridFour @size={{20}} class="text-violet-600" />
            <span>SectionHeader &amp; SectionPanel</span>
          </div>
        </:header>
        <:body>
          <Text @intent="muted" class="mb-4">
            <strong>SectionHeader</strong>
            — icon + title heading with optional subtitle and trailing slot.
            <strong>SectionPanel</strong>
            — full card container with icon header and body.
          </Text>

          <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-4">
            <SectionHeader
              @icon={{PhStar}}
              @title="Section Header"
              @subtitle="With subtitle and trailing content"
            >
              <:trailing>
                <Chip @intent="primary" @size="sm">Badge</Chip>
              </:trailing>
            </SectionHeader>

            <SectionPanel @icon={{PhBriefcase}} @title="Section Panel" @iconColor="violet">
              <:trailing>
                <Button @intent="primary" @size="sm" @appearance="outlined">Action</Button>
              </:trailing>
              <:body>
                <Text @intent="muted">Panel body content goes here. This is a card container with a
                  styled header.</Text>
              </:body>
            </SectionPanel>
          </div>

          <details class="border dark:border-gray-700 rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Code Examples
            </summary>
            <pre
              class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
            ><code>&lt;SectionHeader @icon=&#123;&#123;PhStar&#125;&#125; @title="My Section"
                @subtitle="Optional subtitle"&gt;
                &lt;:trailing&gt;&lt;Chip&gt;Badge&lt;/Chip&gt;&lt;/:trailing&gt;
                &lt;/SectionHeader&gt; &lt;SectionPanel @icon=&#123;&#123;PhBriefcase&#125;&#125;
                @title="Panel Title" @iconColor="violet"&gt; &lt;:body&gt;Content here&lt;/:body&gt;
                &lt;/SectionPanel&gt;</code></pre>
          </details>
        </:body>
      </Section>
    </div>
  </VStack>
</template>
